import { useCallback, useEffect, useMemo, useState } from "react";
import { StickyNote, Plus, Loader2, Save, Trash2, Search, X } from "lucide-react";
import PageHeader from "@/components/dashboard/PageHeader";
import ConfirmDialog from "@/components/dashboard/ConfirmDialog";
import Button from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import { toastError, toastSuccess } from "@/lib/toast";
import { listNotes, createNote, updateNote, deleteNote } from "@/api/notes";
import { formatDateTime } from "@/lib/dates";

const FIELD =
  "w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 focus:border-brand-500 focus:outline-none dark:border-white/10 dark:bg-white/5 dark:text-slate-200";

function fmtWhen(v) {
  return formatDateTime(v, "");
}

// A blank in-editor draft (not yet persisted).
const DRAFT = { id: null, title: "", content: "" };
// Mirrors CONTENT_MAX in backend/api/routers/notes.py.
const CONTENT_MAX = 50000;

export default function Notes() {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null); // { id, title, content } being edited
  const [draftTitle, setDraftTitle] = useState("");
  const [draftContent, setDraftContent] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Deleting a note was a single click on a button styled exactly like Cancel,
  // with no undo and no confirmation.
  const [confirmDel, setConfirmDel] = useState(false);
  // Where we were headed when a dirty editor blocked the way: a note object to
  // open, or the CLOSE sentinel. Deleting a SAVED note asked for confirmation
  // while losing an UNSAVED one — by clicking another note, "New note", or the
  // ✕ — threw the draft away in silence, under a label that said "unsaved
  // changes". That was the one destructive path with no guard on it.
  const [pendingNav, setPendingNav] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listNotes();
      setNotes(Array.isArray(data) ? data : []);
    } catch (e) {
      toastError(e?.message || "Couldn't load your notes.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const open = (note) => {
    setSelected(note);
    setDraftTitle(note.title || "");
    setDraftContent(note.content || "");
  };
  const startNew = () => open(DRAFT);
  const closeEditor = () => { setSelected(null); setDraftTitle(""); setDraftContent(""); };

  const dirty = !!selected && (draftTitle !== (selected.title || "") || draftContent !== (selected.content || ""));
  // `dirty` is part of this now: re-saving an untouched note fired a PUT and
  // reported "Note saved." for a request that changed nothing.
  const canSave = !!selected && (draftTitle.trim() || draftContent.trim()) && dirty && !saving;

  // The in-app guard can't see a tab close, a reload, or a link out of the SPA.
  // Only registered while there is something to lose, so it never nags.
  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  // Every route out of the editor goes through here. `next` is the note to open,
  // or CLOSE_EDITOR to leave it entirely.
  const CLOSE_EDITOR = "__close__";
  const leaveEditor = (next) => {
    if (dirty) { setPendingNav(next); return; }
    if (next === CLOSE_EDITOR) closeEditor();
    else open(next);
  };
  const discardAndGo = () => {
    const next = pendingNav;
    setPendingNav(null);
    if (next === CLOSE_EDITOR) closeEditor();
    else if (next) open(next);
  };

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const payload = { title: draftTitle, content: draftContent };
      const saved = selected.id
        ? await updateNote(selected.id, payload)
        : await createNote(payload);
      setNotes((prev) => {
        const rest = prev.filter((n) => n.id !== saved.id);
        return [saved, ...rest];
      });
      setSelected(saved);
      toastSuccess("Note saved.");
    } catch (e) {
      toastError(e?.message || "Couldn't save the note.");
    } finally {
      setSaving(false);
    }
  };

  // An unsaved draft has nothing to delete — discard it without a dialog.
  const askRemove = () => {
    if (!selected?.id) { closeEditor(); return; }
    setConfirmDel(true);
  };

  const remove = async () => {
    if (!selected?.id || deleting) return;
    setDeleting(true);
    try {
      await deleteNote(selected.id);
      setNotes((prev) => prev.filter((n) => n.id !== selected.id));
      setConfirmDel(false);
      closeEditor();
      toastSuccess("Note deleted.");
    } catch (e) {
      toastError(e?.message || "Couldn't delete the note.");
    } finally {
      setDeleting(false);
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return notes;
    // `${n.title} ${n.content}` stringified nulls, so searching "null" matched
    // every note with an empty field.
    return notes.filter((n) =>
      `${n.title || ""} ${n.content || ""}`.toLowerCase().includes(q));
  }, [notes, query]);

  return (
    <>
      <PageHeader
        title="Notes"
        subtitle="Your personal scratchpad — jot down test ideas, credentials, and reminders."
        actions={<Button size="sm" onClick={() => leaveEditor(DRAFT)}><Plus size={16} /> New note</Button>}
      />

      <div className="grid gap-4 lg:grid-cols-[minmax(0,20rem)_1fr]">
        {/* List */}
        <div className="card overflow-hidden">
          <div className="border-b border-slate-100 p-3 dark:border-white/10">
            <div className="relative">
              <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search notes…"
                className={cn(FIELD, "pl-9")}
              />
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 size={22} className="animate-spin" /></div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center gap-2 px-4 py-14 text-center">
              <span className="grid h-11 w-11 place-items-center rounded-2xl bg-brand-500/10 text-brand-600 dark:text-brand-300">
                <StickyNote size={20} />
              </span>
              <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                {notes.length === 0 ? "No notes yet" : "No matches"}
              </p>
              <p className="max-w-[15rem] text-xs text-slate-500 dark:text-slate-400">
                {notes.length === 0 ? "Click “New note” to write your first one." : "Try a different search."}
              </p>
            </div>
          ) : (
            <ul className="max-h-[32rem] divide-y divide-slate-100 overflow-auto dark:divide-white/5">
              {filtered.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() => leaveEditor(n)}
                    className={cn(
                      "flex w-full flex-col gap-0.5 px-4 py-3 text-left transition-colors hover:bg-slate-50 dark:hover:bg-white/5",
                      selected?.id === n.id && "bg-brand-50/70 dark:bg-brand-500/10",
                    )}
                  >
                    <span className="truncate text-sm font-medium text-slate-800 dark:text-white">
                      {n.title || "Untitled note"}
                    </span>
                    <span className="truncate text-xs text-slate-400">
                      {(n.content || "").replace(/\s+/g, " ").trim() || "No content"}
                    </span>
                    <span className="mt-0.5 text-[0.6875rem] text-slate-400">{fmtWhen(n.updated_at)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Editor */}
        <div className="card p-5">
          {!selected ? (
            <div className="flex h-full min-h-[18rem] flex-col items-center justify-center gap-2 text-center">
              <span className="grid h-12 w-12 place-items-center rounded-2xl bg-gradient-to-br from-brand-500 to-fuchsia-500 text-white shadow-glow">
                <StickyNote size={22} />
              </span>
              <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Select a note to edit</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">…or create a new one.</p>
              <Button size="sm" className="mt-2" onClick={startNew}><Plus size={15} /> New note</Button>
            </div>
          ) : (
            <div className="flex h-full flex-col gap-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-medium uppercase tracking-wide text-slate-400">
                  {selected.id ? "Editing note" : "New note"}
                </span>
                <button type="button" onClick={() => leaveEditor(CLOSE_EDITOR)} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200" title="Close">
                  <X size={16} />
                </button>
              </div>

              <input
                value={draftTitle}
                onChange={(e) => setDraftTitle(e.target.value)}
                placeholder="Title"
                maxLength={255}
                className={cn(FIELD, "text-base font-semibold")}
              />
              {/* maxLength matches CONTENT_MAX on the API, which now refuses
                  anything longer — unbounded, one paste could store an
                  arbitrarily large row. */}
              <textarea
                value={draftContent}
                onChange={(e) => setDraftContent(e.target.value)}
                placeholder="Write your note…"
                rows={14}
                maxLength={CONTENT_MAX}
                className={cn(FIELD, "min-h-[16rem] flex-1 resize-y leading-relaxed")}
              />

              <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-3 dark:border-white/10">
                <span className="text-xs text-slate-400">
                  {selected.id ? `Last saved ${fmtWhen(selected.updated_at)}` : "Not saved yet"}
                  {dirty && <span className="ml-1 text-amber-500">· unsaved changes</span>}
                </span>
                <div className="flex gap-2">
                  {/* variant="danger", not "outline" — Delete was styled exactly
                      like Cancel. The label's leading space is now a real gap. */}
                  <Button variant={selected.id ? "danger" : "outline"} size="sm" onClick={askRemove} disabled={deleting}>
                    {deleting ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                    {selected.id ? "Delete" : "Discard"}
                  </Button>
                  <Button size="sm" onClick={save} disabled={!canSave}>
                    {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Leaving a dirty editor. Same dialog component as the delete guard, so
          the two destructive paths look and behave alike. */}
      <ConfirmDialog
        open={!!pendingNav}
        title="Discard unsaved changes?"
        body={`"${(draftTitle || "").trim() || "Untitled note"}" has changes you haven't saved. They'll be lost.`}
        confirmLabel="Discard changes"
        onConfirm={discardAndGo}
        onCancel={() => setPendingNav(null)}
      />

      <ConfirmDialog
        open={confirmDel}
        title="Delete this note?"
        body={`"${(selected?.title || "").trim() || "Untitled note"}" will be removed permanently. This can't be undone.`}
        confirmLabel="Delete note"
        busy={deleting}
        onConfirm={remove}
        onCancel={() => setConfirmDel(false)}
      />
    </>
  );
}
